import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Button from '@components/button/Button'

import { Viewer, ViperViewProps } from '@components/plan/ViewerComponent'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

interface Props {
  spaceId: string
}

const DEFAULT_INIT: ViperViewProps['initialPosition'] = {
  center: [2.273982551847581, 48.82340600971497],
  zoom: 14,
}

const AdminPlanScreen = ({ spaceId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const plans = useReducer(AdminStore.store, (s) => s.plans)
  const spaces = useReducer(AdminStore.store, (s) => s.spaces)

  const plan = React.useMemo(() => plans.find((r) => r.localisationId === spaceId), [plans, spaceId])
  const space = React.useMemo(() => spaces.find((s) => s.id === spaceId), [spaces, spaceId])

  const onEdit = () => {
    navigation.push(`/admin/plans/form/map?spaceId=${spaceId}`)
  }

  const headerLinks: HeaderLink[] = [
    { label: i18n.t('screens.admin.plans.title'), onClick: () => navigation.push('/admin/plans') },
  ]

  const ModifyButton = {
    label: i18n.t('common.modify'),
    onClick: onEdit,
    status: 'active' as ButtonStatus,
  }

  const renderViewer = (item: PlanViewer) => {
    const initialPosition: ViperViewProps['initialPosition'] | undefined = item.params
      ? {
          center: item.params.center ? [item.params.center.x, item.params.center.y] : undefined,
          zoom: item.params.zoom || undefined,
        }
      : undefined

    return <Viewer key={item.path} planUrl={item.path} pins={[]} initialPosition={initialPosition || DEFAULT_INIT} />
  }

  return (
    <Root header={isPhone && space ? space.libelle : undefined} fixed onLeft={() => navigation.jumpTo('/admin/plans')}>
      {!!plan && !!space ? (
        <>
          {!isPhone && (
            <HeaderContainer>
              <Header title={space.libelle} links={headerLinks} buttons={[ModifyButton]} />
            </HeaderContainer>
          )}

          {plan.viewers.geojson && plan.status === 'ready' ? (
            <Container>{renderViewer(plan.viewers.geojson)}</Container>
          ) : plan.status === 'pending' ? (
            <MainContainer>
              <MissingInfo>{i18n.t('screens.admin.plans.pending')}</MissingInfo>
            </MainContainer>
          ) : (
            <MainContainer>
              <MissingInfo>{i18n.t('common.error')}</MissingInfo>
            </MainContainer>
          )}
        </>
      ) : (
        <MainContainer>
          <MissingInfo>{i18n.t('common.error')}</MissingInfo>
        </MainContainer>
      )}

      {isPhone && (
        <BottomContainer>
          <ButtonContainer>
            <Button {...ModifyButton} />
          </ButtonContainer>
        </BottomContainer>
      )}
    </Root>
  )
}

export default AdminPlanScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const Container = styled(View)`
  flex: 1;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const MainContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `24px 16px` : '24px')};
`

const BottomContainer = styled(View)`
  padding: 16px;
  flex-direction: row;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)<{ isLeft?: boolean }>`
  flex: 1;
  ${(props) => (props.isLeft ? 'margin-right: 8px' : 'marrgin-left: 8px')};
`
